import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CUSTOM_TIME_RANGE_VALUE,
  DEFAULT_ADMIN_TIME_RANGE,
  getAdminTimeRangeOptions,
  parseCustomTimeRange,
  parseFinancialYearOffset,
} from '@/lib/timeRangeOptions';

/** Query keys for page-level filters (shareable deep links). */
export const ADMIN_FILTER_RANGE_PARAM = 'range';
export const ADMIN_FILTER_COUNTRY_PARAM = 'country';
export const ADMIN_FILTER_CURRENCY_PARAM = 'currency';
export const ADMIN_FILTER_TIMEZONE_PARAM = 'timezone';

/**
 * Accept a hand-typed / bookmarked `range` value, or reject it so we fall back
 * to the page default. Bare `custom` is menu-only and never applied.
 */
export function isValidAdminFilterRange(
  value: string,
  options?: { allowFinancialYear?: boolean },
): boolean {
  if (!value || value === CUSTOM_TIME_RANGE_VALUE) return false;
  if (parseCustomTimeRange(value)) return true;
  if (options?.allowFinancialYear && parseFinancialYearOffset(value) != null) return true;
  return getAdminTimeRangeOptions().some((option) => option.value === value);
}

/** ISO country or `AU:VIC` geo selection; empty / garbage → all countries. */
export function parseAdminFilterCountry(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > 32) return '';
  if (!/^[A-Za-z0-9][A-Za-z0-9:_-]*$/.test(trimmed)) return '';
  return trimmed;
}

export function readAdminFilterRange(
  raw: string | null | undefined,
  defaultRange: string,
  options?: { allowFinancialYear?: boolean },
): string {
  if (raw && isValidAdminFilterRange(raw, options)) return raw;
  return defaultRange;
}

/** Uppercase ISO-4217-style code when allow-listed (or any 3-letter code). */
export function readAdminFilterCurrency(
  raw: string | null | undefined,
  defaultCurrency: string,
  allowed?: readonly string[],
): string {
  if (!raw) return defaultCurrency;
  const code = raw.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return defaultCurrency;
  if (allowed && allowed.length > 0 && !allowed.includes(code)) return defaultCurrency;
  return code;
}

/** IANA zone when allow-listed (or a safe Area/City pattern). */
export function readAdminFilterTimezone(
  raw: string | null | undefined,
  defaultTimezone: string,
  allowed?: readonly string[],
): string {
  if (!raw) return defaultTimezone;
  const value = raw.trim();
  if (!value || value.length > 64) return defaultTimezone;
  if (allowed && allowed.length > 0) {
    return allowed.includes(value) ? value : defaultTimezone;
  }
  if (!/^[A-Za-z0-9_+-]+(?:\/[A-Za-z0-9_+-]+)+$/.test(value)) return defaultTimezone;
  return value;
}

type UseAdminPageFiltersOptions = {
  /** Closed-state default when `range` is absent or invalid. */
  defaultRange?: string;
  /** Finance FY presets (`fy-0`…`fy-3`). */
  allowFinancialYear?: boolean;
  /**
   * When set, sync reporting currency to `?currency=`.
   * Omit on pages without a currency filter (Dashboard, Engagement, Moderation).
   */
  defaultCurrency?: string;
  /** Restrict `currency` to these codes (e.g. AUD/USD). */
  allowedCurrencies?: readonly string[];
  /**
   * When set, sync heatmap timezone to `?timezone=`.
   * Omit on pages without a timezone filter (Finance, Moderation).
   */
  defaultTimezone?: string;
  /** Restrict `timezone` to these IANA ids (heatmap allow-list). */
  allowedTimezones?: readonly string[];
};

/**
 * Page-level filters mirrored to query params so a single HTTP link opens the
 * exact filtered view. Defaults are omitted from the URL. Writes use `replace`
 * so filter tweaks do not stack history entries.
 *
 * Always syncs `range` + `country`. Optionally syncs `currency` / `timezone`
 * when the matching default is provided.
 */
export function useAdminPageFilters(options?: UseAdminPageFiltersOptions) {
  const defaultRange = options?.defaultRange ?? DEFAULT_ADMIN_TIME_RANGE;
  const allowFinancialYear = options?.allowFinancialYear ?? false;
  const defaultCurrency = options?.defaultCurrency;
  const allowedCurrencies = options?.allowedCurrencies;
  const defaultTimezone = options?.defaultTimezone;
  const allowedTimezones = options?.allowedTimezones;
  const trackCurrency = defaultCurrency != null;
  const trackTimezone = defaultTimezone != null;
  const [params, setParams] = useSearchParams();

  const [range, setRangeState] = useState(() =>
    readAdminFilterRange(params.get(ADMIN_FILTER_RANGE_PARAM), defaultRange, {
      allowFinancialYear,
    }),
  );
  const [countryCode, setCountryCodeState] = useState(() =>
    parseAdminFilterCountry(params.get(ADMIN_FILTER_COUNTRY_PARAM)),
  );
  const [currency, setCurrencyState] = useState(() =>
    trackCurrency
      ? readAdminFilterCurrency(
          params.get(ADMIN_FILTER_CURRENCY_PARAM),
          defaultCurrency,
          allowedCurrencies,
        )
      : '',
  );
  const [timezone, setTimezoneState] = useState(() =>
    trackTimezone
      ? readAdminFilterTimezone(
          params.get(ADMIN_FILTER_TIMEZONE_PARAM),
          defaultTimezone,
          allowedTimezones,
        )
      : '',
  );

  const setRange = useCallback((next: string) => {
    setRangeState(next);
  }, []);
  const setCountryCode = useCallback((next: string) => {
    setCountryCodeState(next);
  }, []);
  const setCurrency = useCallback((next: string) => {
    setCurrencyState(next);
  }, []);
  const setTimezone = useCallback((next: string) => {
    setTimezoneState(next);
  }, []);

  useEffect(() => {
    const currencyPristine = !trackCurrency || currency === defaultCurrency;
    const timezonePristine = !trackTimezone || timezone === defaultTimezone;
    const pristine =
      range === defaultRange && countryCode === '' && currencyPristine && timezonePristine;
    const hadAny =
      params.has(ADMIN_FILTER_RANGE_PARAM) ||
      params.has(ADMIN_FILTER_COUNTRY_PARAM) ||
      (trackCurrency && params.has(ADMIN_FILTER_CURRENCY_PARAM)) ||
      (trackTimezone && params.has(ADMIN_FILTER_TIMEZONE_PARAM));
    if (pristine && !hadAny) return;

    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (range === defaultRange) next.delete(ADMIN_FILTER_RANGE_PARAM);
        else next.set(ADMIN_FILTER_RANGE_PARAM, range);
        if (!countryCode) next.delete(ADMIN_FILTER_COUNTRY_PARAM);
        else next.set(ADMIN_FILTER_COUNTRY_PARAM, countryCode);
        if (trackCurrency) {
          if (currency === defaultCurrency) next.delete(ADMIN_FILTER_CURRENCY_PARAM);
          else next.set(ADMIN_FILTER_CURRENCY_PARAM, currency);
        }
        if (trackTimezone) {
          if (timezone === defaultTimezone) next.delete(ADMIN_FILTER_TIMEZONE_PARAM);
          else next.set(ADMIN_FILTER_TIMEZONE_PARAM, timezone);
        }
        return next.toString() === prev.toString() ? prev : next;
      },
      { replace: true },
    );
    // Seed from URL once; thereafter only mirror local state → URL (same pattern as useServerTable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, countryCode, currency, timezone, defaultRange, defaultCurrency, defaultTimezone]);

  return {
    range,
    setRange,
    countryCode,
    setCountryCode,
    currency,
    setCurrency,
    timezone,
    setTimezone,
  };
}
